// src/context/AuthProvider.jsx
// STATUS: active | UPDATED: 2026-08-28
// LOG:
//   2026-08-28  New. useAuth was a plain hook with its own useState, and twelve
//               components called it, so the app opened twelve independent auth
//               subscriptions and fired up to twelve getUserProfile queries on
//               every load and again on every auth event. Twelve components
//               asking the same question and keeping twelve separate answers.
//
//               It also made the profile impossible to update. Editing your
//               display name in Settings changed Settings' copy of the profile
//               and nothing else, so the header still greeted you by the old
//               name until a full reload. There was no way to refresh, because
//               there was no shared thing to refresh.
//
//               One subscription, one profile, one refreshProfile() that every
//               consumer sees. useAuth keeps its import path and its shape, so
//               no call site changed.
//
// Anything that needs the signed-in user reads it through useAuth, which now
// reads this. The provider is mounted once, in main.jsx, above the router.

import { createContext, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { getCurrentSession, getUserProfile, onAuthStateChange } from '../lib/auth'

export const AuthContext = createContext(null)

export function AuthProvider({ children }) {
  const [session, setSession] = useState(null)
  const [profile, setProfile] = useState(null)
  const [loading, setLoading] = useState(true)
  // A recovery link grants a real session before any new password is chosen.
  // That is how Supabase recovery works and it is fine, but only if the app then
  // insists on the password being set. Without this the person is simply signed
  // in, which means a forwarded reset email is a working login. Tyler hit exactly
  // that on 2026-08-30 using a link sent from the Supabase dashboard, which
  // redirects to the bare site root rather than to /reset-password/.
  const [recovering, setRecovering] = useState(
    typeof window !== 'undefined' &&
      (window.location.hash.includes('type=recovery') ||
        new URLSearchParams(window.location.search).get('type') === 'recovery')
  )
  const mounted = useRef(true)

  // Pull the profile row for a user id. Kept separate so refreshProfile and the
  // auth listener share exactly one code path.
  const loadProfile = useCallback(async (userId) => {
    if (!userId) {
      if (mounted.current) setProfile(null)
      return null
    }
    const { data } = await getUserProfile(userId)
    if (mounted.current) setProfile(data)
    return data
  }, [])

  // Call after writing to the users row so every consumer sees the new value.
  // Returns the fresh profile for callers that want to act on it.
  const refreshProfile = useCallback(async () => {
    const id = session?.user?.id
    if (!id) return null
    return loadProfile(id)
  }, [session?.user?.id, loadProfile])

  useEffect(() => {
    mounted.current = true

    // setLoading(false) lives in the finally on purpose. It used to sit after
    // two unguarded awaits, so anything that threw or stalled in front of it
    // left the whole app behind a spinner with no way out but a reload. The
    // gate opening is not conditional on the session arriving: a signed out
    // render is recoverable and an infinite spinner is not.
    async function loadInitialSession() {
      try {
        const initialSession = await getCurrentSession()
        if (!mounted.current) return
        setSession(initialSession)
        if (initialSession?.user) await loadProfile(initialSession.user.id)
      } catch (err) {
        console.error('auth: bootstrap failed', err.message)
      } finally {
        if (mounted.current) setLoading(false)
      }
    }

    loadInitialSession()

    const { data: { subscription } } = onAuthStateChange(async (event, newSession) => {
      if (!mounted.current) return
      // Latches on. It is cleared only by a successful password update, so no
      // later SIGNED_IN event can quietly let somebody past the gate.
      if (event === 'PASSWORD_RECOVERY') setRecovering(true)
      if (event === 'SIGNED_OUT') setRecovering(false)
      setSession(newSession)
      try {
        await loadProfile(newSession?.user?.id)
      } catch (err) {
        console.error('auth: profile load on auth change failed', err.message)
      }
      // The listener is also the recovery path. When a slow refresh lands after
      // the bootstrap gave up waiting, this is what lets the person in.
      if (mounted.current) setLoading(false)
    })

    return () => {
      mounted.current = false
      subscription.unsubscribe()
    }
  }, [loadProfile])

  const value = useMemo(
    () => ({
      user: session?.user || null,
      session,
      profile,
      dealership: profile?.dealership || null,
      role: profile?.role || null,
      isAdmin: profile?.role === 'admin',
      isAuthenticated: !!session,
      // A signed-in user with no row in public.users. They authenticate and then
      // land nowhere, because role and dealership are both null. Worth naming so
      // the UI can say something true instead of rendering a stranger.
      profileMissing: !!session && !profile,
      // True between clicking a reset link and choosing a new password.
      // ProtectedRoute sends these people to /reset-password/ and nowhere else.
      mustSetPassword: !!session && recovering,
      clearRecovery: () => setRecovering(false),
      loading,
      refreshProfile,
    }),
    [session, profile, loading, recovering, refreshProfile]
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
